/**
 * The intake analytics strip — one number, and where every figure came from.
 *
 * Register P1, in Chris's words: Emailed Intake showed twenty-two records, the
 * Ingestion Queue five and the deal list thirteen, "a spread duplicates alone
 * do not explain." The reconciliation that answers him existed on the server
 * and was rendered nowhere, so the three disagreeing numbers stayed on screen
 * and the one true number stayed in an endpoint nobody called.
 *
 * Two rules this component exists to keep:
 *
 *   1. ONE number leads. Everything else is a subset of it with a stated basis.
 *   2. Clicking a figure shows the records it counted. The server returns the
 *      ids behind each tile and the page filters to exactly those, so a figure
 *      and its rows are the same set by construction — never two predicates
 *      that agree today and drift next release.
 *
 * It replaces a strip whose values were `intakeDemoMetrics()` — literal zeros
 * and a hard-coded 4.2 on any live account. A fabricated metric on a live
 * screen is worse than no metric.
 */
import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { AlertTriangle, CheckCircle2, FileSearch, Inbox, Layers, MailX, XCircle } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

import { ingestionService, type IntakeAnalytic } from '@/services/ingestion'
import ReconciliationSurfaces from '@/components/shared/ReconciliationSurfaces'

/** Tone per tile: attention where a person is needed, quiet where nothing is. */
const TONE: Record<string, { icon: LucideIcon; dot: string; value: string }> = {
  in_intake: { icon: FileSearch, dot: 'bg-sky-400/10 text-sky-300', value: 'text-stone-100' },
  smart_review: { icon: AlertTriangle, dot: 'bg-amber-400/10 text-amber-300', value: 'text-amber-200' },
  on_pipeline: { icon: CheckCircle2, dot: 'bg-emerald-400/10 text-emerald-300', value: 'text-stone-100' },
  duplicates: { icon: Layers, dot: 'bg-violet-400/10 text-violet-300', value: 'text-stone-100' },
  machine_mail: { icon: MailX, dot: 'bg-stone-500/10 text-stone-400', value: 'text-stone-300' },
  declined: { icon: XCircle, dot: 'bg-stone-500/10 text-stone-400', value: 'text-stone-300' },
  // A kill the desk made on purpose (one by one, or the parallel-run reset).
  killed: { icon: XCircle, dot: 'bg-stone-500/10 text-stone-400', value: 'text-stone-300' },
  broken_link: { icon: AlertTriangle, dot: 'bg-red-400/10 text-red-300', value: 'text-red-300' },
}

const FALLBACK = { icon: Inbox, dot: 'bg-stone-500/10 text-stone-400', value: 'text-stone-100' }

export interface IntakeAnalyticsStripProps {
  /** The tile currently driving the on-screen filter, if any. */
  activeKey: string | null
  /** Selecting a tile, or the same tile again to clear it. */
  onSelect: (analytic: IntakeAnalytic | null) => void
  /** Demo accounts show no live figures rather than invented ones. */
  demo: boolean
  /** Optional control rendered on the same row as the analytic tiles (e.g. the review opener). */
  trailing?: ReactNode
}

export default function IntakeAnalyticsStrip({
  activeKey,
  onSelect,
  demo,
  trailing,
}: IntakeAnalyticsStripProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['intake-reconciliation'],
    queryFn: () => ingestionService.getReconciliation(),
    enabled: !demo,
    refetchInterval: 30_000,
  })

  if (demo) return null

  if (isError) {
    return (
      <div className="metric-cell flex-shrink-0 text-[10px] text-stone-500">
        Intake figures are unavailable — the reconciliation could not be read. The
        lists below are unaffected.
      </div>
    )
  }

  if (isLoading || !data) {
    return (
      <div className="grid flex-shrink-0 grid-cols-4 gap-3" aria-busy="true">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="metric-cell h-[52px] animate-pulse" />
        ))}
      </div>
    )
  }

  const shown = data.analytics.filter((a) => a.count > 0 || a.key === 'in_intake')
  // The grid sits under one number and the reader adds it up, so only the
  // figures that partition that number may be drawn in it. Declined and
  // machine-generated mail were once drawn there while counting records other
  // tiles already held — 44 and 45 against a headline of 43 (stress test C12).
  // Anything that does not partition is an 'of which', shown beside the grid.
  const partitioning = shown.filter((a) => a.partitions !== false)
  const crossCutting = shown.filter((a) => a.partitions === false)
  const unique = data.uniqueDeals

  return (
    <div className="flex-shrink-0 space-y-2">
      {/* The one number. Everything below is a subset of it. */}
      <div className="flex items-baseline gap-3">
        <span className="metric-cell-label">Intake records</span>
        <span className="text-[22px] font-semibold leading-none text-stone-100">
          {data.total}
        </span>
        {/* Records are not deals: a request and its re-sends and replies are
            one deal. The number the desk compares with Backshop is this one
            (Louis, 22 Sep: count by unique deal, not re-send). Its pipeline
            half is the pipeline grid's own count, hand-entered deals included
            and named, so the two screens cannot disagree (stress test C12). */}
        {unique ? (
          <span
            className="flex items-baseline gap-1.5"
            title={unique.basis}
            data-testid="intake-unique-deals"
          >
            <span className="metric-cell-label">Unique deals</span>
            <span className="text-[22px] font-semibold leading-none text-amber-200">
              {unique.count}
            </span>
            <span className="text-[10px] text-stone-500">
              {unique.onPipeline} on the pipeline
              {unique.notFromIntake ? ` (${unique.notFromIntake} not from intake)` : ''}
              {' · '}
              {unique.inIntake} waiting in intake
            </span>
          </span>
        ) : null}
        <span className="min-w-0 flex-1 truncate text-[10px] text-stone-500" title={data.basis}>
          {data.basis}
        </span>
        {!data.accounted && (
          <span className="flex items-center gap-1 text-[10px] text-amber-300">
            <AlertTriangle className="h-3 w-3" />
            figures below do not account for every record
          </span>
        )}
      </div>

      <div className="flex items-stretch gap-2">
        <div
          className="grid min-w-0 flex-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6"
          role="group"
          aria-label="Intake analytics — select a figure to see the records behind it"
        >
          {partitioning.map((a) => {
            const tone = TONE[a.key] ?? FALLBACK
            const Icon = tone.icon
            const active = activeKey === a.key
            return (
              <button
                key={a.key}
                type="button"
                onClick={() => onSelect(active ? null : a)}
                aria-pressed={active}
                title={`${a.basis}. Select to show these ${a.count} record${a.count === 1 ? '' : 's'}.`}
                className={[
                  'metric-cell flex items-center gap-2 text-left transition-colors',
                  'focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400',
                  active
                    ? 'border-amber-400/40 bg-amber-400/[0.06]'
                    : 'hover:border-amber-400/20',
                ].join(' ')}
              >
                <span className={`rounded p-1.5 ${tone.dot}`}>
                  <Icon className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="metric-cell-label block truncate">{a.label}</span>
                  <span className={`metric-cell-value ${tone.value}`}>{a.count}</span>
                </span>
              </button>
            )
          })}
        </div>
        {trailing ? <div className="flex shrink-0 items-stretch">{trailing}</div> : null}
      </div>

      {crossCutting.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-stone-500"
          data-testid="intake-cross-cutting"
        >
          <span>Of which, counted above as well:</span>
          {crossCutting.map((a) => (
            <button
              key={a.key}
              type="button"
              onClick={() => onSelect(activeKey === a.key ? null : a)}
              aria-pressed={activeKey === a.key}
              title={`${a.basis}. Select to show these ${a.count} record${a.count === 1 ? '' : 's'}.`}
              className="text-stone-400 underline decoration-dotted hover:text-stone-200"
            >
              {a.label} {a.count}
            </button>
          ))}
        </div>
      )}

      {/* Register P1: the reasons have to be ON SCREEN, not in an endpoint. */}
      <ReconciliationSurfaces total={data.total} surfaces={data.surfaces} />

      {activeKey && (
        <div className="flex items-center gap-2 text-[10px] text-stone-500">
          <span className="text-amber-300">
            Showing the {shown.find((a) => a.key === activeKey)?.count ?? 0} records behind
            “{shown.find((a) => a.key === activeKey)?.label}”
          </span>
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="text-stone-400 underline decoration-dotted hover:text-stone-200"
          >
            show all
          </button>
        </div>
      )}
    </div>
  )
}
